import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import {
  Search,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  Download,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import IPOService from "@/services/IPO/APIs/IPOService";
import { format, isValid } from "date-fns";
import { notify } from "@/common/components/common/Notification";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import { IPOList } from "@/utils/types/IPO/ipo";
import Loader from "@/common/components/ui/loader";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import { Label } from "@/common/components/ui/label";
import { Separator } from "@/common/components/ui/separator";
import * as XLSX from "xlsx";

const INDUSTRY_EMPTY_VALUE = "__industry_unspecified__";

const IPOManagement = () => {
  const navigate = useNavigate();
  const [ipos, setIpos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishFilter, setPublishFilter] = useState<
    "all" | "published" | "unpublished"
  >("all");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [ipoToDelete, setIpoToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const formatDate = (dateStr: string) => {
    if (!dateStr) {
      return "N/A";
    }
    const date = new Date(dateStr);
    return isValid(date) ? format(date, "d MMM yyyy") : "N/A";
  };

  const formatIndianAmount = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    const num =
      typeof value === "number"
        ? value
        : Number(String(value).replace(/,/g, "").trim());
    if (Number.isNaN(num)) {
      return "N/A";
    }
    return num.toLocaleString("en-IN");
  };

  // Helper function to map API response to component expected format
  const mapIPOToComponentFormat = (ipo: IPOList): any => {
    // Format listing_status: "open" -> "Open", "upcoming" -> "Upcoming", etc.
    // const formatStatus = (status: string): string => {
    //   if (!status) {
    //     return "N/A";
    //   }
    //   return status.charAt(0).toUpperCase() + status.slice(1);
    // };

    // Format ipo_type: "main_board" -> "Main Board", "sme" -> "SME"
    const formatIPOType = (ipo: IPOList): string => {
      if (ipo.nsE_SubType === "SME") {
        return "SME";
      }
      return "Main Board";
    };

    // Format issue size: string to "₹X,XXX.XX Cr"
    const formatIssueSize = (size: string | number | undefined): string => {
      if (!size) {
        return "N/A";
      }
      const numSize = typeof size === "string" ? parseFloat(size) : size;
      if (isNaN(numSize)) {
        return "N/A";
      }
      return `₹${numSize.toLocaleString("en-IN")}`;
    };

    // Calculate status based on bidding dates
    const calculateStatus = (startStr: string, endStr: string): string => {
      if (!startStr || !endStr) {
        return "N/A";
      }

      const today = new Date();

      const startDate = new Date(startStr);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(endStr);
      endDate.setHours(16, 45, 0, 0);

      if (today < startDate) {
        return "Upcoming";
      }
      if (today > endDate) {
        return "Closed";
      }
      return "Open";
    };

    const raw = ipo as unknown as Record<string, unknown>;
    const industryRaw =
      raw.industrySector ?? raw.industry_sector ?? raw.industry ?? "";
    const industry =
      typeof industryRaw === "string"
        ? industryRaw.trim()
        : String(industryRaw ?? "").trim();

    return {
      // Keep all original API fields first
      ...ipo,
      // Override with mapped/transformed fields for component display
      company_short_name: ipo.name,
      symbol: ipo.symbol,
      industry,
      ipo_type: formatIPOType(ipo),
      price_min: ipo.minPrice || "N/A",
      price_max: ipo.maxPrice || "N/A",
      lot_size: ipo.lotSize,
      issue_size: formatIssueSize(ipo.issueSize),
      bid_start_date: formatDate(ipo.biddingStartDate),
      bid_end_date: formatDate(ipo.biddingEndDate),
      status: calculateStatus(ipo.biddingStartDate, ipo.biddingEndDate),
      isPublish: ipo.isPublish ?? false,
    };
  };

  const fetchIPOs = async () => {
    setLoading(true);
    try {
      // Fetch all IPOs with a large page size so we can filter and paginate client-side
      const response = await IPOService.list(currentPage, pageSize, {
        search: searchTerm,
        ipoStatus: statusFilter,
      });
      // Extract results from IPOListResponse
      let ipoData: IPOList[] = [];
      if (response?.results && Array.isArray(response.results)) {
        ipoData = response.results;
        setTotalCount(response.count || 0);
      } else if (Array.isArray(response)) {
        ipoData = response;
        setTotalCount(response.length);
      }

      // Map API data to component format
      if (ipoData.length > 0) {
        const mappedData = ipoData.map(mapIPOToComponentFormat);
        setIpos(mappedData);
      } else {
        setIpos([]);
      }
    } catch (error) {
      console.error("Error fetching IPOs:", error);
      notify.error("Error", "Failed to fetch IPOs");
      setIpos([]);
    } finally {
      setLoading(false);
    }
  };

  const syncIPOs = async () => {
    setLoading(true);
    try {
      const response = await IPOService.syncIPO();
      if (response.success) {
        setLoading(false);
        notify.success("Success", response.message);
      } else {
        setLoading(false);
        notify.error("Error", response.message);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error syncing IPOs:", error);
      notify.error("Error", "Failed to sync IPOs");
    }
  };

  // Re-fetch when server-side filters or pagination changes
  useEffect(() => {
    fetchIPOs();
  }, [searchTerm, statusFilter, currentPage, pageSize]);

  // Reset to page 1 when server-side filters change (search/status)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (searchTerm.trim()) {
      n += 1;
    }
    if (typeFilter !== "all") {
      n += 1;
    }
    if (statusFilter !== "all") {
      n += 1;
    }
    if (publishFilter !== "all") {
      n += 1;
    }
    if (selectedIndustries.length > 0) {
      n += 1;
    }
    return n;
  }, [
    searchTerm,
    typeFilter,
    statusFilter,
    publishFilter,
    selectedIndustries.length,
  ]);

  // Memoize filtered IPOs (by search, status, type, published, industry) for consistent counts
  const filteredIPOs = useMemo(() => {
    return ipos.filter(ipo => {
      const searchableValues = [
        ipo.company_short_name,
        ipo.name,
        ipo.symbol,
        ipo.ipo_type,
        ipo.status,
        ipo.issue_size,
        ipo.bid_start_date,
        ipo.bid_end_date,
        ipo.industry,
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase());

      const matchesSearch =
        normalizedSearchTerm.length === 0 ||
        searchableValues.some(value => value.includes(normalizedSearchTerm));
      // Map API filter values to computed display status values
      const apiToDisplayStatus: Record<string, string> = {
        live: "Open",
        upcoming: "Upcoming",
        close: "Closed",
      };
      const matchesStatus =
        statusFilter === "all" ||
        ipo.status === (apiToDisplayStatus[statusFilter] ?? statusFilter);
      const matchesType = typeFilter === "all" || ipo.ipo_type === typeFilter;
      const matchesPublish =
        publishFilter === "all"
          ? true
          : publishFilter === "published"
            ? ipo.isPublish === true
            : ipo.isPublish === false;
      const matchesIndustry =
        selectedIndustries.length === 0 ||
        selectedIndustries.some(sel => {
          if (sel === INDUSTRY_EMPTY_VALUE) {
            return !(ipo.industry ?? "").trim();
          }
          return (ipo.industry ?? "").trim() === sel;
        });
      return (
        matchesSearch &&
        matchesStatus &&
        matchesType &&
        matchesPublish &&
        matchesIndustry
      );
    });
  }, [
    ipos,
    normalizedSearchTerm,
    statusFilter,
    typeFilter,
    publishFilter,
    selectedIndustries,
  ]);

  // Reset to page 1 when client-side filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, publishFilter, selectedIndustries]);

  // Server already returns the correct page; just apply client-side filters on top
  const paginatedIPOs = filteredIPOs;

  /**
   * Use the API total count when no client-side filters are active
   * (accurate across all pages). Switch to filteredIPOs.length the moment
   * type / publish / industry filters reduce visible rows, so the count
   * always matches what is actually shown in the table.
   */
  const hasClientFilters =
    typeFilter !== "all" ||
    publishFilter !== "all" ||
    selectedIndustries.length > 0;
  const displayCount = hasClientFilters ? filteredIPOs.length : totalCount;

  // Memoize stats calculations to prevent multiple filter operations on every render
  // const stats = useMemo(() => {
  //   const openCount = ipos.filter(i => i.status === "Open").length;
  //   const upcomingCount = ipos.filter(i => i.status === "Upcoming").length;
  //   const closedListedCount = ipos.filter(
  //     i => i.status === "Closed" || i.status === "Listed"
  //   ).length;
  //   const mainBoardCount = ipos.filter(i => i.ipo_type === "Main Board").length;

  //   return [
  //     {
  //       label: "Total IPOs",
  //       value: totalCount,
  //       icon: TrendingUp,
  //       color: "bg-[#1D2C56]/10 text-[#1D2C56]",
  //       change: "+3 this month",
  //     },
  //     {
  //       label: "Open Now",
  //       value: openCount,
  //       icon: CheckCircle,
  //       color: "bg-[#66A0CD]/20 text-[#1D2C56]",
  //       change: "Active subscriptions",
  //     },
  //     {
  //       label: "Upcoming",
  //       value: upcomingCount,
  //       icon: Clock,
  //       color: "bg-[#66A0CD]/10 text-[#66A0CD]",
  //       change: "Scheduled",
  //     },
  //     {
  //       label: "Closed/Listed",
  //       value: closedListedCount,
  //       icon: XCircle,
  //       color: "bg-slate-100 text-slate-600",
  //       change: "Completed",
  //     },
  //     {
  //       label: "Main Board",
  //       value: mainBoardCount,
  //       icon: Building2,
  //       color: "bg-[#1D2C56]/20 text-[#1D2C56]",
  //       change: "Large cap",
  //     },
  //   ];
  // }, [ipos]);

  const handleDeleteClick = (id: string, name: string) => {
    setIpoToDelete({ id, name });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ipoToDelete) {
      return;
    }

    try {
      setActionLoading(true);
      await IPOService.delete(ipoToDelete.id);
      notify.success(
        "IPO Deleted",
        `${ipoToDelete.name} has been removed successfully.`
      );
      setDeleteModalOpen(false);
      setIpoToDelete(null);
      fetchIPOs();
    } catch (error) {
      console.error("Error deleting IPO:", error);
      notify.error("Error", "Failed to delete IPO");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setIpoToDelete(null);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPublishFilter("all");
    setSelectedIndustries([]);
  };

  const handleExportIPOs = () => {
    const exportList = selectedRows.length > 0 ? selectedRows : [];

    if (exportList.length === 0) {
      notify.error("No Data", "Please select rows to export.");
      return;
    }

    const rows = exportList.map(ipo => {
      const industryExport = (ipo.industry ?? "").trim();
      return {
        Company: ipo.company_short_name ?? "",
        Symbol: ipo.symbol ?? "",
        Type: ipo.ipo_type ?? "",
        "Price Min": ipo.price_min ?? "",
        "Price Max": ipo.price_max ?? "",
        "Lot Size": ipo.lot_size ?? "",
        "Issue Size": ipo.issue_size ?? "",
        "Bid Start Date": ipo.bid_start_date ?? "",
        "Bid End Date": ipo.bid_end_date ?? "",
        Status: ipo.status ?? "",
        Published: ipo.isPublish ? "Yes" : "No",
        Industry: industryExport.length > 0 ? industryExport : "Unspecified",
        Exchange: ipo.exchange ?? "",
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "IPO Management");

    const datePart = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `ipo_management_${datePart}.xlsx`);
    notify.success("Exported", `${rows.length} IPO(s) exported successfully.`);
  };

  // const handleDuplicate = async (ipo: any) => {
  //   const payload = {
  //     ...ipo,
  //     id: `${ipo.id}-copy-${Date.now()}`,
  //     company_short_name: `${ipo.company_short_name ?? ""} (Copy)`,
  //   };
  //   try {
  //     setActionLoading(true);
  //     await IPOService.create(payload);
  //     notify.success(
  //       "IPO Duplicated",
  //       `${ipo.company_short_name ?? ""} has been duplicated as draft.`
  //     );
  //     fetchIPOs();
  //   } catch (error) {
  //     console.error("Error duplicating IPO:", error);
  //     notify.error("Error", "Failed to duplicate IPO");
  //   } finally {
  //     setActionLoading(false);
  //   }
  // };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Open: "bg-[#66A0CD]/20 text-[#1D2C56] border-[#66A0CD]/50 px-3 py-1.5",
      Upcoming: "bg-[#66A0CD]/5 text-[#66A0CD] border-[#66A0CD]/30 px-3 py-1.5",
      Closed: "bg-slate-100 text-slate-500 border-slate-200 px-3 py-1.5",
      Listed: "bg-[#1D2C56] text-white border-[#1D2C56] px-3 py-1.5",
    };
    return (
      <Badge
        className={styles[status] ?? "bg-slate-100 px-3 py-1 text-slate-700"}
        variant="outline"
      >
        {status}
      </Badge>
    );
  };

  // Column configurations for Ant Design Table
  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        width: 500,
        sorter: (a, b) => {
          const nameA = (a.company_short_name ?? "").toLowerCase();
          const nameB = (b.company_short_name ?? "").toLowerCase();
          return nameA.localeCompare(nameB);
        },
        render: (_, ipo) => (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-accent/20">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-secondary">
                {ipo.company_short_name ?? "N/A"}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {ipo.symbol ?? "N/A"}
              </p>
            </div>
          </div>
        ),
      },
      {
        title: "Type",
        dataIndex: "ipo_type",
        key: "type",
        width: 120,
        filters: [
          { text: "Main Board", value: "Main Board" },
          { text: "SME", value: "SME" },
        ],
        onFilter: (value, record) => record.ipo_type === value,
        render: (_, ipo) => (
          <Badge
            variant={null as any}
            className={`border-2 ${ipo.ipo_type === "SME" ? "border-purple-200 text-purple-700" : "border-gray-200"}`}
          >
            {ipo.ipo_type ?? "N/A"}
          </Badge>
        ),
      },
      {
        title: "Price Range",
        dataIndex: "price_range",
        key: "price_range",
        width: 200,
        sorter: (a, b) => {
          const priceA = parseFloat(a.price_min) || 0;
          const priceB = parseFloat(b.price_min) || 0;
          return priceA - priceB;
        },
        render: (_, ipo) => (
          <span className="font-medium">
            ₹{formatIndianAmount(ipo.price_min)} - ₹
            {formatIndianAmount(ipo.price_max)}
          </span>
        ),
      },
      {
        title: "Lot Size",
        dataIndex: "lot_size",
        key: "lot_size",
        width: 160,
        sorter: (a, b) => (a.lot_size ?? 0) - (b.lot_size ?? 0),
        render: (_, ipo) => (ipo.lot_size ? `${ipo.lot_size} shares` : "N/A"),
      },
      {
        title: "Issue Size",
        dataIndex: "issue_size",
        key: "issue_size",
        width: 150,
        sorter: (a, b) => {
          const sizeA = parseFloat(String(a.total_issue_size ?? 0));
          const sizeB = parseFloat(String(b.total_issue_size ?? 0));
          return sizeA - sizeB;
        },
        render: (_, ipo) => (
          <span className="font-semibold text-primary">
            {ipo.issue_size ?? "N/A"}
          </span>
        ),
      },
      {
        title: "Bid Dates",
        dataIndex: "bid_dates",
        key: "bid_dates",
        width: 250,
        sorter: (a, b) => {
          const dateA = new Date(a.bid_start_date ?? 0).getTime();
          const dateB = new Date(b.bid_start_date ?? 0).getTime();
          return dateA - dateB;
        },
        render: (_, ipo) => (
          <div className="flex flex-col text-sm">
            <span className="font-semibold text-secondary">
              {ipo.bid_start_date ?? "N/A"} to {ipo.bid_end_date ?? "N/A"}
            </span>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
        filters: [
          { text: "Open", value: "Open" },
          { text: "Upcoming", value: "Upcoming" },
          { text: "Closed", value: "Closed" },
          { text: "Listed", value: "Listed" },
        ],
        onFilter: (value, record) => record.status === value,
        render: (_, ipo) => getStatusBadge(ipo.status ?? "N/A"),
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        fixed: "right" as const,
        render: (_, ipo) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.IPO_VIEW(ipo.id))
              }
              title="View IPO"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.EDIT_IPO(ipo.id))
              }
              title="Edit IPO"
            >
              <Edit className="h-4 w-4" />
            </Button>
            {/* <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={e => {
                e.stopPropagation();
                handleDuplicate(ipo);
              }}
              title="Duplicate IPO"
            >
              <Copy className="h-4 w-4" />
            </Button> */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(ipo.id, ipo.company_short_name);
              }}
              title="Delete IPO"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === "type") {
      setTypeFilter(value);
    } else if (filterType === "status") {
      setStatusFilter(value);
    } else if (filterType === "publish") {
      setPublishFilter(value as "all" | "published" | "unpublished");
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      {(loading || actionLoading) && (
        <Loader
          message={
            loading
              ? "Syncing IPOs..."
              : actionLoading
                ? "Processing request..."
                : "Processing your request..."
          }
        />
      )}
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <TrendingUp className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                IPO Management
              </h1>
              <p className="text-white/70">
                Create, edit, and manage all Initial Public Offerings
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => syncIPOs()}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExportIPOs}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
            {/* x */}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {/* <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {stats.map(stat => (
          <Card
            key={stat.label}
            className="rounded-2xl border bg-card p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="mb-3 flex items-start gap-3">
              <div className={`rounded-xl p-2.5 ${stat.color}`}>
                <stat.icon className="h-5 w-5" />
              </div>
              <p className="text-2xl font-bold text-secondary">{stat.value}</p>
            </div>
            <p className="mt-1 text-xs font-medium text-muted-foreground">
              {stat.label}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              {stat.change}
            </p>
          </Card>
        ))}
      </div> */}

      {/* Filters — single responsive row */}
      <Card className="rounded-2xl border border-border/80 bg-card shadow-sm">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="flex flex-wrap items-end gap-3 lg:gap-4">
            {/* Search */}
            <div className="w-full min-w-[min(100%,220px)] flex-[1.15] sm:min-w-[240px]">
              <Label
                htmlFor="ipo-filter-search"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                Search
              </Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="ipo-filter-search"
                  placeholder="Company name or symbol"
                  className="h-10 border-border/90 bg-background pl-9 shadow-sm focus-visible:ring-1"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  aria-label="Search IPOs by company name or symbol"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* IPO Type */}
            <div className="w-full min-w-[140px] sm:w-[160px]">
              <Label
                htmlFor="ipo-filter-type"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                IPO type
              </Label>
              <Select
                value={typeFilter}
                onValueChange={value => handleFilterChange("type", value)}
              >
                <SelectTrigger
                  id="ipo-filter-type"
                  className="h-10 w-full border-border/90 bg-background shadow-sm"
                  aria-label="Filter by IPO board type"
                >
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="Main Board">Main Board</SelectItem>
                  <SelectItem value="SME">SME</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full min-w-[140px] sm:w-[180px]">
              <Label
                htmlFor="ipo-filter-status"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                Status
              </Label>
              <Select
                value={statusFilter}
                onValueChange={value => handleFilterChange("status", value)}
              >
                <SelectTrigger
                  id="ipo-filter-status"
                  className="h-10 w-full border-border/90 bg-background shadow-sm"
                  aria-label="Filter by IPO board type"
                >
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="live">Open</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="close">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full min-w-[140px] sm:w-[180px]">
              <Label
                htmlFor="ipo-filter-publish"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                Publish
              </Label>
              <Select
                value={publishFilter}
                onValueChange={value => handleFilterChange("publish", value)}
              >
                <SelectTrigger
                  id="ipo-filter-publish"
                  className="h-10 w-full border-border/90 bg-background shadow-sm"
                  aria-label="Filter by publish status"
                >
                  <SelectValue placeholder="All publish status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All publish status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="unpublished">Unpublished</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reset */}
            <div className="ml-auto flex w-full min-w-[120px] items-end sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={resetFilters}
                className="h-10 w-full gap-2 border-dashed border-primary/40 bg-background font-medium text-secondary shadow-sm hover:bg-muted/60 sm:w-auto"
                aria-label="Reset all filters"
                title="Clear search, type, status, publish, and industry filters"
              >
                <RotateCcw className="h-4 w-4 shrink-0" aria-hidden />
                Reset
                {activeFilterCount > 0 ? (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 rounded-full px-1.5 py-0 text-[10px] font-semibold"
                  >
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </div>
          </div>

          <Separator className="bg-border/80" />

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <p>
              Showing{" "}
              <span className="font-semibold text-secondary">
                {displayCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              </span>
              –
              <span className="font-semibold text-secondary">
                {Math.min(currentPage * pageSize, displayCount)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-secondary">
                {displayCount}
              </span>{" "}
              IPOs
            </p>
            {activeFilterCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                {activeFilterCount} active filter
                {activeFilterCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* IPO Table */}
      <AntDTable
        data={paginatedIPOs}
        columns={columns}
        rowKey="id"
        loading={loading}
        showSettings
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
          preserveSelectedRowKeys: false,
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: displayCount,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size ?? pageSize);
          },
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} IPOs`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 1200 }}
        emptyText="No IPOs found"
        className="ant-table-custom"
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete IPO"
        message="Are you sure you want to delete"
        itemName={ipoToDelete?.name ?? ""}
        isLoading={actionLoading}
        confirmText="Yes, Delete"
        cancelText="No"
      />
    </div>
  );
};

export default IPOManagement;
